import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDiagramEditor } from '../hooks/useDiagramEditor';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';

/**
 * DiagramEditorPanel - Draw.io editor panel
 *
 * Desktop: right-side resizable panel (same pattern as PdfViewerPanel)
 * Touch/Mobile: fullscreen overlay
 *
 * Communicates with Draw.io embed via postMessage protocol.
 * User can edit diagram and export SVG/PNG to local machine.
 */

const MIN_WIDTH = 400;
const DEFAULT_WIDTH = 600;
const STORAGE_KEY = 'diagramEditorPanelWidth';
const DRAWIO_ORIGIN = 'https://embed.diagrams.net';

const DiagramEditorPanel = () => {
  const { isOpen, diagramXml, diagramTitle, close, updateXml } = useDiagramEditor();

  const getSavedWidth = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  };

  const [width, setWidth] = useState(getSavedWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [iframeReady, setIframeReady] = useState(false);
  const iframeRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const latestXmlRef = useRef(null);
  const pendingExportRef = useRef(null);

  // Detect touch devices
  useEffect(() => {
    const checkTouch = () => {
      const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const small = window.innerWidth < 768;
      setIsTouch(touch || small);
    };
    checkTouch();
    window.addEventListener('resize', checkTouch);
    return () => window.removeEventListener('resize', checkTouch);
  }, []);

  // Slide animation
  useEffect(() => {
    if (isOpen && diagramXml) {
      setShouldRender(true);
      setIframeReady(false);
      latestXmlRef.current = diagramXml;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
      setIframeReady(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, diagramXml]);

  // Save width to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, width.toString());
  }, [width]);

  // Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  // Prevent body scroll when fullscreen (use class to avoid background repaint bug)
  useEffect(() => {
    if (isOpen && isTouch) {
      document.body.classList.add('viewer-open');
    } else {
      document.body.classList.remove('viewer-open');
    }
    return () => { document.body.classList.remove('viewer-open'); };
  }, [isOpen, isTouch]);

  // postMessage handler for Draw.io communication
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.origin.includes('diagrams.net')) return;
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) return;

      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }

      switch (msg.event) {
        case 'init':
          // Draw.io ready — load the diagram
          setIframeReady(true);
          if (iframeRef.current && latestXmlRef.current) {
            const sanitizedXml = latestXmlRef.current
              .replace(/&(?!amp;|lt;|gt;|quot;|apos;|#\d+;|#x[\da-fA-F]+;)/g, '&amp;');
            iframeRef.current.contentWindow.postMessage(
              JSON.stringify({ action: 'load', xml: sanitizedXml, autosave: 1 }),
              DRAWIO_ORIGIN
            );
          }
          break;

        case 'autosave':
          // Track latest edits
          if (msg.xml) {
            latestXmlRef.current = msg.xml;
          }
          break;

        case 'save':
          // User clicked save
          if (msg.xml) {
            latestXmlRef.current = msg.xml;
            updateXml(msg.xml);
          }
          if (msg.exit) {
            close();
          }
          break;

        case 'exit':
          close();
          break;

        case 'export':
          // Export response from Draw.io
          if (msg.data && pendingExportRef.current) {
            const { format, filename } = pendingExportRef.current;
            pendingExportRef.current = null;

            if (format === 'svg') {
              const blob = new Blob([msg.data], { type: 'image/svg+xml' });
              downloadBlob(blob, filename);
            } else if (format === 'png') {
              // msg.data is base64 with data:image/png;base64, prefix
              const base64 = msg.data.split(',')[1] || msg.data;
              const binary = atob(base64);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
              const blob = new Blob([bytes], { type: 'image/png' });
              downloadBlob(blob, filename);
            }
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [close, updateXml]);

  // Download helper
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Export actions
  const exportSvg = () => {
    if (!iframeRef.current || !iframeReady) return;
    const filename = `${(diagramTitle || 'diagram').replace(/[^a-zA-Z0-9_-]/g, '_')}.svg`;
    pendingExportRef.current = { format: 'svg', filename };
    iframeRef.current.contentWindow.postMessage(
      JSON.stringify({ action: 'export', format: 'xmlsvg', spin: 'Exporting...' }),
      DRAWIO_ORIGIN
    );
  };

  const exportPng = () => {
    if (!iframeRef.current || !iframeReady) return;
    const filename = `${(diagramTitle || 'diagram').replace(/[^a-zA-Z0-9_-]/g, '_')}.png`;
    pendingExportRef.current = { format: 'png', filename };
    iframeRef.current.contentWindow.postMessage(
      JSON.stringify({ action: 'export', format: 'png', spin: 'Exporting...', scale: 2 }),
      DRAWIO_ORIGIN
    );
  };

  // Drag resize handlers (desktop only)
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
  }, [width]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const maxWidth = window.innerWidth * 0.7;
    const deltaX = startXRef.current - e.clientX;
    const newWidth = Math.min(maxWidth, Math.max(MIN_WIDTH, startWidthRef.current + deltaX));
    setWidth(newWidth);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!shouldRender) return null;

  // Detect dark mode
  const isDark = document.documentElement.getAttribute('data-theme') !== 'light' &&
    !document.documentElement.classList.contains('light');

  const embedUrl = `${DRAWIO_ORIGIN}/?embed=1&proto=json&spin=1&libraries=1&saveAndExit=1&noExitBtn=0${isDark ? '&dark=1' : ''}`;

  const panelTransform = isTouch
    ? (isVisible ? 'translateY(0)' : 'translateY(100%)')
    : (isVisible ? 'translateX(0)' : 'translateX(100%)');

  return (
    <div
      className={`diagram-editor-panel ${isTouch ? 'diagram-editor-panel--mobile' : 'diagram-editor-panel--desktop'}`}
      style={{
        ...(!isTouch && { width: `${width}px` }),
        transform: panelTransform,
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Drag Handle - desktop only */}
      {!isTouch && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-blue-500 transition-colors group"
          onMouseDown={handleMouseDown}
        >
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-12 -ml-1.5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex gap-0.5">
              <ChevronLeft size={12} className="text-blue-500" />
              <ChevronRight size={12} className="text-blue-500" />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="relative flex items-center justify-between px-4 py-2 border-b"
        style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))', backgroundColor: 'var(--bg-secondary, #1e1e2e)' }}>
        <h3 className="font-medium truncate max-w-[50%] text-sm" style={{ color: 'var(--text-primary)' }} title={diagramTitle}>
          {diagramTitle}
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={exportSvg}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-white/10 transition-colors"
            style={{ color: 'var(--text-secondary)' }}
            title="Export SVG (vector)"
          >
            <Download size={14} />
            SVG
          </button>
          <button
            onClick={exportPng}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-white/10 transition-colors"
            style={{ color: 'var(--text-secondary)' }}
            title="Export PNG (image)"
          >
            <Download size={14} />
            PNG
          </button>
          <button
            onClick={close}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors ml-2"
            title="Close (Esc)"
          >
            <X size={18} style={{ color: 'var(--text-secondary)' }} />
          </button>
        </div>
      </div>

      {/* Draw.io Editor iframe */}
      <div className="flex-1 overflow-hidden">
        <iframe
          ref={iframeRef}
          src={embedUrl}
          className="w-full h-full border-0"
          title="Diagram Editor"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
      </div>
    </div>
  );
};

export default DiagramEditorPanel;
